/**
 * PortfolioSwitcher — dropdown on the Home hero + sidebar for enterprise
 * brands (`manages_multiple_brands=true`).
 *
 * Lets a brand team toggle which sub-brand they're viewing. The active
 * sub-brand is persisted on the backend via POST /api/brand/multi-portfolio/active
 * so the choice survives a page reload.
 *
 * Self-hiding: if the brand has `manages_multiple_brands=false` OR the
 * portfolio is empty, the component renders nothing.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, Building2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortfolioSwitcher({ compact = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/brand/multi-portfolio`, { withCredentials: true });
        setData(r.data);
      } catch {
        setData({ manages_multiple_brands: false, sub_brands: [] });
      }
    })();
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  if (!data || !data.manages_multiple_brands || !data.sub_brands?.length) return null;

  const parent = data.parent_brand;
  const active = data.active_sub_brand_id
    ? data.sub_brands.find(s => s.id === data.active_sub_brand_id)
    : null;
  const activeLabel = active?.name || parent?.name || 'Parent brand';

  const setActive = async (subId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/brand/multi-portfolio/active`,
        { sub_brand_id: subId }, { withCredentials: true });
      setData(d => ({ ...d, active_sub_brand_id: subId }));
    } catch (err) {
      // Surface to the user via toast — silent failure left the switcher
      // looking broken when /multi-portfolio/active 5xx'd (iter 86.1).
      try {
        const { toast } = await import('sonner');
        toast.error('Could not switch portfolio — please retry.');
      } catch { /* sonner not available — keep DOM clean */ }
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative inline-block" data-testid="portfolio-switcher">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        data-testid="portfolio-switcher-trigger"
        className={`inline-flex items-center gap-2 rounded-full transition-all hover:brightness-110 disabled:opacity-60 ${
          compact ? 'px-3 py-1.5 text-[10px]' : 'px-4 py-2 text-[11px]'
        }`}
        style={{
          background: 'rgba(255,77,148,0.08)',
          border: '1px solid rgba(255,77,148,0.35)',
          color: '#fff',
        }}
      >
        <Building2 className="h-3 w-3 text-[#FF4D94]" />
        <span className="font-black uppercase tracking-wider truncate max-w-[180px]">{activeLabel}</span>
        <span className="text-[9px] text-gray-500 font-semibold">
          {data.sub_brands.length + 1} brands
        </span>
        <ChevronDown className="h-3 w-3 text-gray-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-[280px] rounded-xl overflow-hidden z-40"
          style={{ background: '#0F0F0F', border: '1px solid #1f1f1f', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}
          data-testid="portfolio-switcher-menu"
        >
          <p className="px-4 py-2.5 text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold border-b border-[#1f1f1f]">
            Switch view · persists across sessions
          </p>
          <Option
            label={parent?.name || 'Parent brand'}
            vertical={parent?.vertical}
            active={!data.active_sub_brand_id}
            onClick={() => setActive(null)}
            testId="portfolio-switcher-option-parent"
            kicker="Parent"
          />
          {data.sub_brands.map(sb => (
            <Option
              key={sb.id}
              label={sb.name}
              vertical={sb.vertical}
              active={data.active_sub_brand_id === sb.id}
              onClick={() => setActive(sb.id)}
              testId={`portfolio-switcher-option-${sb.id}`}
              kicker="Sub-brand"
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Option({ label, vertical, active, onClick, testId, kicker }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#1a1a1a] text-left transition-colors"
    >
      <Building2 className="h-3.5 w-3.5 text-[#FF4D94] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-[12px] font-black text-white truncate">{label}</p>
          {active && <Check className="h-3 w-3 text-[#4ade80]" />}
        </div>
        <p className="text-[9px] text-gray-500 uppercase tracking-wider mt-0.5">
          {kicker}{vertical ? ` · ${vertical}` : ''}
        </p>
      </div>
    </button>
  );
}
